import type {
  WorkflowDefinitionDocument,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
  WorkflowActionTrigger,
} from './platform-workflow-definition-lifecycle.models';

export interface WorkflowStepPlacementPath {
  edgeId: string;
  fromNodeId: string;
  toNodeId: string;
  label: string;
  eligible: boolean;
  reason: string | null;
}

type Command = NonNullable<
  WorkflowDefinitionNode['declaration']
>['behavior']['commands'][number];
interface OutcomeOwner {
  nodeId: string;
  commandIndex: number;
  outcomeIndex: number;
}

const editableSchemas = new Set([
  '1.3',
  '1.4',
  '1.5',
  '1.6',
  '1.7',
  '1.8',
  '1.9',
  '1.10',
  '1.11',
  '1.12',
  '1.13',
  '1.14',
  '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22']);
const resultLabels: Record<string, string> = {
  approved: 'Approved',
  changes_required: 'Changes required',
};

function nodes(document: WorkflowDefinitionDocument): WorkflowDefinitionNode[] {
  return (document.nodes ?? []).filter(
    (node) => node && typeof node.id === 'string',
  );
}
function edges(document: WorkflowDefinitionDocument): WorkflowDefinitionEdge[] {
  return (document.edges ?? []).filter(
    (edge) => edge && typeof edge.id === 'string',
  );
}
function node(
  document: WorkflowDefinitionDocument,
  id: string,
): WorkflowDefinitionNode {
  const matches = nodes(document).filter((item) => item.id === id);
  if (typeof id !== 'string' || !id.trim() || matches.length !== 1)
    throw new Error(
      'Choose one existing, unambiguous work stage. A path endpoint is missing or duplicated.',
    );
  return matches[0];
}
function path(
  document: WorkflowDefinitionDocument,
  id: string,
): WorkflowDefinitionEdge {
  const matches = edges(document).filter((item) => item.id === id);
  if (typeof id !== 'string' || !id.trim() || matches.length !== 1)
    throw new Error(
      'This path is missing or has an ambiguous identity. Refresh the path selection.',
    );
  return matches[0];
}
function commands(item: WorkflowDefinitionNode): Command[] {
  if (!item.declaration) return [];
  const items = item.declaration.behavior?.commands;
  if (
    !Array.isArray(items) ||
    items.some(
      (command) =>
        !command?.capability?.key || !Array.isArray(command.outcomes),
    )
  )
    throw new Error(
      'The step has incomplete action settings. Repair them before changing its paths.',
    );
  return items;
}
function isLegacyPreparation(item: WorkflowDefinitionNode): boolean {
  return commands(item).some(
    (command) =>
      command.capability.key.provider === 'workflow' &&
      command.capability.key.kind === 'command' &&
      command.capability.key.code === 'workflow.preparation.submit',
  );
}
function hasManagedOutcomes(item: WorkflowDefinitionNode): boolean {
  return commands(item).some((command) =>
    command.outcomes.some((outcome) => !!outcome?.destinationNodeId),
  );
}
function label(document: WorkflowDefinitionDocument, id: string): string {
  const matches = nodes(document).filter((item) => item.id === id);
  if (matches.length !== 1) return id || 'Unavailable stage';
  const item = matches[0];
  return item.typeCode === 'entry'
    ? 'Start process'
    : item.typeCode === 'terminal'
      ? 'Complete workflow'
      : item.displayName?.trim() || item.stepCode || item.id;
}
function reaches(
  items: readonly WorkflowDefinitionEdge[],
  start: string,
  target: string,
): boolean {
  const pending = [start],
    seen = new Set<string>();
  while (pending.length) {
    const current = pending.pop()!;
    if (current === target) return true;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const item of items)
      if (item.pathKindCode == null && item.fromNodeId === current)
        pending.push(item.toNodeId);
  }
  return false;
}
function ensureEditable(document: WorkflowDefinitionDocument): void {
  if (!editableSchemas.has(document.payloadSchemaVersion))
    throw new Error('Enable editable routing before changing connected paths.');
}
function triggerContextIsValid(
  document: WorkflowDefinitionDocument,
  trigger: WorkflowActionTrigger,
): boolean {
  const humans = nodes(document).filter(
    (item) => item.id === trigger.nodeId && item.typeCode === 'human_step',
  );
  if (humans.length !== 1) return false;
  if (trigger.kind === 'transition' || trigger.kind === 'return') {
    const matches = edges(document).filter(
      (item) => item.id === trigger.edgeId,
    );
    if (
      matches.length !== 1 ||
      (trigger.kind === 'return' && matches[0].pathKindCode !== 'return')
    )
      return false;
    const source = nodes(document).filter(
      (item) => item.id === matches[0].fromNodeId,
    );
    return (
      trigger.outcomeCode == null &&
      (matches[0].fromNodeId === trigger.nodeId ||
        (source.length === 1 &&
          !!source[0].routing &&
          edges(document).some(
            (item) =>
              item.fromNodeId === trigger.nodeId &&
              item.toNodeId === source[0].id,
          )))
    );
  }
  if (trigger.kind === 'decision')
    return (
      trigger.edgeId == null &&
      !!trigger.outcomeCode?.trim() &&
      edges(document).some(
        (item) =>
          item.fromNodeId === trigger.nodeId &&
          nodes(document).some(
            (router) =>
              router.id === item.toNodeId &&
              !!router.routing &&
              edges(document).some(
                (route) =>
                  route.fromNodeId === router.id &&
                  route.outcomeCode === trigger.outcomeCode,
              ),
          ),
      )
    );
  return (
    ['entry', 'exit', 'assignment-change'].includes(trigger.kind) &&
    trigger.edgeId == null &&
    trigger.outcomeCode == null
  );
}

/** Resolve a mirrored action destination only through one immediate owner, never by latest/first match. */
function outcomeOwner(
  document: WorkflowDefinitionDocument,
  item: WorkflowDefinitionEdge,
): OutcomeOwner | null {
  const router = node(document, item.fromNodeId);
  if (router.typeCode !== 'routing_decision') return null;
  const incoming = edges(document).filter(
    (edge) => edge.toNodeId === router.id,
  );
  const owners = incoming.map((edge) => node(document, edge.fromNodeId));
  const managed = owners.filter(
    (owner) => owner.typeCode === 'human_step' && hasManagedOutcomes(owner),
  );
  if (!managed.length) return null;
  if (incoming.length !== 1 || managed.length !== 1)
    throw new Error(
      'This result path has a shared or ambiguous action owner. Edit its outcome settings first.',
    );
  const owner = managed[0],
    items = commands(owner);
  const routedCommands = items
    .map((command, index) => ({ command, index }))
    .filter(({ command }) =>
      command.outcomes.some((outcome) => !!outcome?.destinationNodeId),
    );
  if (
    routedCommands.length !== 1 ||
    !router.routing?.conditionCode ||
    !(router.routing.conditionVersion > 0)
  )
    throw new Error(
      'This result path has incomplete or ambiguous action routing. Edit its outcome settings first.',
    );
  const { command, index } = routedCommands[0];
  if (
    edges(document).filter((edge) => edge.fromNodeId === owner.id).length !==
      1 ||
    incoming[0].outcomeCode != null ||
    incoming[0].pathKindCode != null ||
    command.outcomes.some((outcome) => !outcome?.code) ||
    new Set(command.outcomes.map((outcome) => outcome.code)).size !==
      command.outcomes.length
  )
    throw new Error(
      'This result path has ambiguous action routing. Edit its outcome settings first.',
    );
  const matches = command.outcomes
    .map((outcome, outcomeIndex) => ({ outcome, outcomeIndex }))
    .filter(({ outcome }) => outcome?.code === item.outcomeCode);
  if (
    matches.length !== 1 ||
    matches[0].outcome.destinationNodeId !== item.toNodeId
  )
    throw new Error(
      'The action outcome and this path disagree. Edit its outcome settings before changing the path.',
    );
  return {
    nodeId: owner.id,
    commandIndex: index,
    outcomeIndex: matches[0].outcomeIndex,
  };
}
function updateOwner(
  document: WorkflowDefinitionDocument,
  owner: OutcomeOwner | null,
  destination: string,
): WorkflowDefinitionNode[] {
  if (!owner) return document.nodes;
  return document.nodes.map((item) =>
    item.id !== owner.nodeId
      ? item
      : {
          ...item,
          declaration: {
            ...item.declaration!,
            behavior: {
              ...item.declaration!.behavior,
              commands: item.declaration!.behavior.commands.map(
                (command, commandIndex) =>
                  commandIndex !== owner.commandIndex
                    ? command
                    : {
                        ...command,
                        outcomes: command.outcomes.map(
                          (outcome, outcomeIndex) =>
                            outcomeIndex !== owner.outcomeIndex
                              ? outcome
                              : {
                                  ...outcome,
                                  destinationNodeId: destination,
                                },
                        ),
                      },
              ),
            },
          },
        },
  );
}
function generatedId(
  document: WorkflowDefinitionDocument,
  item: WorkflowDefinitionEdge,
): string {
  const stem =
    `path.${item.fromNodeId}.${item.outcomeCode || 'next'}.${item.toNodeId}`
      .replace(/[^a-zA-Z0-9._-]/g, '-')
      .slice(0, 108);
  let id = stem,
    suffix = 2;
  while (edges(document).some((edge) => edge.id === id))
    id = `${stem}-${suffix++}`;
  return id;
}
function validateChangedPath(
  document: WorkflowDefinitionDocument,
  item: WorkflowDefinitionEdge,
  all: WorkflowDefinitionEdge[],
  previous?: WorkflowDefinitionEdge,
): void {
  const source = node(document, item.fromNodeId),
    target = node(document, item.toNodeId);
  if (
    !['entry', 'human_step', 'automated_step', 'routing_decision'].includes(
      source.typeCode,
    )
  )
    throw new Error('A completion point cannot have an outgoing path.');
  if (
    !['human_step', 'automated_step', 'terminal', 'routing_decision'].includes(
      target.typeCode,
    )
  )
    throw new Error(
      'A path must lead to work, a routing decision, or a completion point; it cannot lead back to Start.',
    );
  if (item.pathKindCode != null && item.pathKindCode !== 'return')
    throw new Error('Choose a forward path or Return for correction.');
  const outgoing = all.filter((edge) => edge.fromNodeId === source.id);
  if (source.typeCode === 'automated_step') {
    if (!source.automation) {
      // A newly placed draft step retains its intended next stage until an
      // installed command supplies its actual business outcomes. Publication
      // still requires the complete automated-step contract.
      if (
        outgoing.length !== 1 ||
        item.pathKindCode != null ||
        item.outcomeCode != null
      )
        throw new Error(
          'An unfinished automated step can retain only one forward connection. Configure its activity before adding outcome paths.',
        );
    } else {
      const outcomes = source.automation.outcomes.map(
        (outcome) => outcome.routeOutcome,
      );
      if (
        !outcomes.length ||
        item.pathKindCode != null ||
        !item.outcomeCode ||
        !outcomes.includes(item.outcomeCode) ||
        outgoing.length !== outcomes.length ||
        new Set(outgoing.map((edge) => edge.outcomeCode)).size !==
          outcomes.length ||
        outgoing.some(
          (edge) => !edge.outcomeCode || !outcomes.includes(edge.outcomeCode),
        )
      )
        throw new Error(
          'Configure one explicit forward path for each automated business outcome in its activity settings.',
        );
    }
  } else if (source.typeCode !== 'routing_decision') {
    if (
      outgoing.length !== 1 ||
      item.outcomeCode != null ||
      item.pathKindCode != null
    )
      throw new Error(
        'A work step has one forward path. Configure an action outcome to create a branch or return.',
      );
    if (
      isLegacyPreparation(source) &&
      (!previous || previous.toNodeId !== item.toNodeId)
    )
      throw new Error(
        'This submission must connect directly to its review step. Use Outcomes & routing to edit its preparation handoff.',
      );
    if (
      hasManagedOutcomes(source) &&
      (!previous || previous.toNodeId !== item.toNodeId)
    )
      throw new Error(
        'Keep this action connected to its result decision. Use Outcomes & routing to edit its destinations.',
      );
  } else {
    if (
      !source.routing?.conditionCode ||
      !(source.routing.conditionVersion > 0) ||
      !item.outcomeCode?.trim()
    )
      throw new Error(
        'A routing decision needs a configured result for this path.',
      );
    if (
      outgoing.filter((edge) => edge.outcomeCode === item.outcomeCode)
        .length !== 1
    )
      throw new Error('Each routing result must have exactly one path.');
    const existingResults = edges(document)
      .filter((edge) => edge.fromNodeId === source.id)
      .map((edge) => edge.outcomeCode);
    const managedCommands = edges(document)
      .filter((edge) => edge.toNodeId === source.id)
      .flatMap((edge) => commands(node(document, edge.fromNodeId)))
      .flatMap((command) => command.outcomes.map((outcome) => outcome.code));
    const allowed =
      source.routing.conditionCode === 'review.result' &&
      source.routing.conditionVersion === 1
        ? ['approved', 'changes_required']
        : [...existingResults, ...managedCommands];
    const retainedResult =
      previous?.fromNodeId === item.fromNodeId &&
      previous.outcomeCode === item.outcomeCode;
    if (!allowed.includes(item.outcomeCode) && !retainedResult)
      throw new Error(
        'This result is not declared by the installed action. Configure its outcomes before adding a branch.',
      );
  }
  if (target.typeCode === 'routing_decision') {
    const incoming = all.filter((edge) => edge.toNodeId === target.id);
    const managed = incoming.filter((edge) => {
      const owner = node(document, edge.fromNodeId);
      return owner.typeCode === 'human_step' && hasManagedOutcomes(owner);
    });
    if (managed.length && (incoming.length !== 1 || managed.length !== 1))
      throw new Error(
        'Keep an action result decision connected to its one owning step. Use Outcomes & routing to edit its destinations.',
      );
  }
  if (item.pathKindCode === 'return') {
    if (
      source.typeCode !== 'routing_decision' ||
      target.typeCode !== 'human_step' ||
      !reaches(all, target.id, source.id)
    )
      throw new Error(
        'A return must lead from a routing decision to earlier human work on its forward path.',
      );
  } else if (reaches(all, target.id, source.id))
    throw new Error(
      'This path would create a forward cycle. Use an explicit return to earlier work.',
    );
}

/** Change one path while preserving its identity and any exact mirrored outcome destination. */
export function replaceWorkflowPath(
  document: WorkflowDefinitionDocument,
  edge: WorkflowDefinitionEdge,
  previousEdgeId?: string,
): WorkflowDefinitionDocument {
  const previous =
    previousEdgeId === undefined ? undefined : path(document, previousEdgeId);
  const next: WorkflowDefinitionEdge = {
    ...previous,
    ...edge,
    id: previous?.id || edge.id?.trim() || generatedId(document, edge),
    outcomeCode:
      edge.outcomeCode === undefined
        ? (previous?.outcomeCode ?? null)
        : edge.outcomeCode?.trim() || null,
    pathKindCode:
      edge.pathKindCode === undefined
        ? (previous?.pathKindCode ?? null)
        : edge.pathKindCode?.trim() || null,
  };
  if (next.id.length > 120 || !next.id.trim())
    throw new Error(
      'Path identities must contain between 1 and 120 characters.',
    );
  if (edges(document).some((item) => item !== previous && item.id === next.id))
    throw new Error(
      'Another path already has this identity. Refresh and try again.',
    );
  const all = previous
    ? document.edges.map((item) => (item === previous ? next : item))
    : [...document.edges, next];
  if (!editableSchemas.has(document.payloadSchemaVersion)) {
    if (!['1.0', '1.1', '1.2'].includes(document.payloadSchemaVersion))
      ensureEditable(document);
    node(document, next.fromNodeId);
    node(document, next.toNodeId);
    if (next.outcomeCode != null || next.pathKindCode != null)
      throw new Error(
        'Enable editable routing before configuring result or return paths.',
      );
    if (previous?.outcomeCode === undefined) delete next.outcomeCode;
    if (previous?.pathKindCode === undefined) delete next.pathKindCode;
    return { ...document, edges: all };
  }
  const previousSources = previous
    ? nodes(document).filter((item) => item.id === previous.fromNodeId)
    : [];
  if (previousSources.length > 1) node(document, previous!.fromNodeId);
  const previousSource = previousSources[0];
  if (
    previous &&
    previousSource?.typeCode === 'human_step' &&
    (previous.fromNodeId !== next.fromNodeId ||
      previous.toNodeId !== next.toNodeId) &&
    (isLegacyPreparation(previousSource) || hasManagedOutcomes(previousSource))
  )
    throw new Error(
      'Keep this action connected to its configured handoff. Use Outcomes & routing to edit its destinations.',
    );
  const oldOwner =
    previous && previousSource?.typeCode === 'routing_decision'
      ? outcomeOwner(document, previous)
      : null;
  if (
    oldOwner &&
    (previous!.fromNodeId !== next.fromNodeId ||
      previous!.outcomeCode !== next.outcomeCode)
  )
    throw new Error(
      'Change this action result in its outcome settings; its source and result identity must be retained.',
    );
  // Existing missing endpoints can be repaired. New managed routes must already agree with their declared outcome.
  const owner =
    oldOwner ??
    (!previous || previous.fromNodeId !== next.fromNodeId
      ? outcomeOwner(document, next)
      : null);
  validateChangedPath(document, next, all.filter(Boolean), previous);
  if (
    previous &&
    previousSource &&
    previous.fromNodeId !== next.fromNodeId &&
    ['entry', 'human_step'].includes(previousSource.typeCode)
  ) {
    const retained = all.filter(
      (item) => item?.fromNodeId === previousSource.id,
    );
    if (
      retained.length !== 1 ||
      retained[0].outcomeCode != null ||
      retained[0].pathKindCode != null
    )
      throw new Error(
        'Moving this path would leave its previous step without one forward path. Reconnect its destination instead.',
      );
  }
  for (const retainedReturn of edges(document)) {
    if (retainedReturn === previous || retainedReturn.pathKindCode !== 'return')
      continue;
    const returnSource = nodes(document).filter(
      (item) => item.id === retainedReturn.fromNodeId,
    );
    const returnTarget = nodes(document).filter(
      (item) => item.id === retainedReturn.toNodeId,
    );
    if (
      returnSource.length === 1 &&
      returnSource[0].typeCode === 'routing_decision' &&
      returnTarget.length === 1 &&
      returnTarget[0].typeCode === 'human_step' &&
      reaches(
        edges(document),
        retainedReturn.toNodeId,
        retainedReturn.fromNodeId,
      ) &&
      !reaches(
        all.filter(Boolean),
        retainedReturn.toNodeId,
        retainedReturn.fromNodeId,
      )
    )
      throw new Error(
        'This change would disconnect an existing return from its earlier work. Update the correction routing first.',
      );
  }
  const candidate = {
    ...document,
    nodes: updateOwner(document, owner, next.toNodeId),
    edges: all,
  };
  for (const trigger of document.triggers ?? [])
    if (
      trigger &&
      triggerContextIsValid(document, trigger) &&
      !triggerContextIsValid(candidate, trigger)
    )
      throw new Error(
        'This change would disconnect an existing notification or action trigger. Update its event settings first.',
      );
  return candidate;
}

function insertionReason(
  document: WorkflowDefinitionDocument,
  item: WorkflowDefinitionEdge,
): string | null {
  try {
    ensureEditable(document);
    path(document, item.id);
    const source = node(document, item.fromNodeId),
      target = node(document, item.toNodeId);
    if (item.pathKindCode === 'return')
      throw new Error(
        'A return revisits earlier work. Insert on a forward path instead.',
      );
    if (item.pathKindCode != null)
      throw new Error(
        'This path has unsupported behavior. Repair it before inserting a step.',
      );
    if (!['human_step', 'automated_step', 'terminal'].includes(target.typeCode))
      throw new Error(
        'Keep the action connected directly to its routing decision. Choose one result path after that decision.',
      );
    if (source.typeCode === 'human_step' && isLegacyPreparation(source))
      throw new Error(
        'This submission must connect directly to its review step. Choose a different path.',
      );
    if (source.typeCode === 'human_step' && hasManagedOutcomes(source))
      throw new Error(
        'Keep the action connected directly to its result decision. Use Outcomes & routing to repair its destinations first.',
      );
    validateChangedPath(document, item, edges(document), item);
    outcomeOwner(document, item);
    return null;
  } catch (error) {
    return error instanceof Error
      ? error.message
      : 'This path cannot be edited safely.';
  }
}

/** Include disabled choices so authors can understand why a path cannot be split. */
export function workflowStepPlacementPaths(
  document: WorkflowDefinitionDocument,
): WorkflowStepPlacementPath[] {
  return (document.edges ?? []).map((item) => {
    const edge = item ?? { id: '', fromNodeId: '', toNodeId: '' };
    const reason = insertionReason(document, edge);
    return {
      edgeId: edge.id || '',
      fromNodeId: edge.fromNodeId || '',
      toNodeId: edge.toNodeId || '',
      label: `${edge.outcomeCode ? `${resultLabels[edge.outcomeCode] || edge.outcomeCode}: ` : ''}${label(document, edge.fromNodeId)} → ${label(document, edge.toNodeId)}`,
      eligible: reason === null,
      reason,
    };
  });
}

const maximumOrder = 2147483647;
function validOrder(value: number): boolean {
  return (
    Number.isInteger(value) && value >= -2147483648 && value <= maximumOrder
  );
}
function insertionOrders(
  document: WorkflowDefinitionDocument,
  source: WorkflowDefinitionNode,
  target: WorkflowDefinitionNode,
  inserted: WorkflowDefinitionNode,
): WorkflowDefinitionNode[] {
  const retained = nodes(document),
    occupied = new Set(retained.map((item) => item.displayOrder));
  // Server display orders are integers. Keep all retained orders whenever there is room.
  if (
    validOrder(source.displayOrder) &&
    validOrder(target.displayOrder) &&
    source.displayOrder < target.displayOrder
  ) {
    const midpoint = Math.floor(
      source.displayOrder + (target.displayOrder - source.displayOrder) / 2,
    );
    if (
      midpoint > source.displayOrder &&
      midpoint < target.displayOrder &&
      !occupied.has(midpoint)
    )
      return [...document.nodes, { ...inserted, displayOrder: midpoint }];
    let firstFree = source.displayOrder + 1;
    while (occupied.has(firstFree) && firstFree < target.displayOrder)
      firstFree++;
    if (firstFree < target.displayOrder)
      return [...document.nodes, { ...inserted, displayOrder: firstFree }];
  }
  // On one linear process, shift only the necessary suffix when adjacent orders have no gap.
  const starts = retained.filter((item) => item.typeCode === 'entry');
  const sequence: WorkflowDefinitionNode[] = [],
    seen = new Set<string>();
  let cursor: WorkflowDefinitionNode | undefined =
    starts.length === 1 ? starts[0] : undefined;
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    sequence.push(cursor);
    const outgoing = edges(document).filter(
      (item) => item.fromNodeId === cursor!.id,
    );
    if (cursor.typeCode === 'terminal') {
      cursor = undefined;
      break;
    }
    if (
      outgoing.length !== 1 ||
      outgoing[0].outcomeCode != null ||
      outgoing[0].pathKindCode != null
    )
      break;
    const matches = retained.filter((item) => item.id === outgoing[0].toNodeId);
    cursor = matches.length === 1 ? matches[0] : undefined;
  }
  const sourceIndex = sequence.findIndex((item) => item.id === source.id);
  if (
    sequence.length === retained.length &&
    sequence.at(-1)?.typeCode === 'terminal' &&
    edges(document).length === retained.length - 1 &&
    retained.every((item) =>
      ['entry', 'human_step', 'automated_step', 'terminal'].includes(
        item.typeCode,
      ),
    ) &&
    sourceIndex >= 0 &&
    sequence[sourceIndex + 1]?.id === target.id &&
    new Set(retained.map((item) => item.id)).size === retained.length
  ) {
    const ordered = [
      ...sequence.slice(0, sourceIndex + 1),
      inserted,
      ...sequence.slice(sourceIndex + 1),
    ];
    const orders = new Map<string, number>();
    let last = -2147483649;
    for (const item of ordered) {
      const order =
        item === inserted ||
        !validOrder(item.displayOrder) ||
        item.displayOrder <= last
          ? last + 1
          : item.displayOrder;
      orders.set(item.id, order);
      last = order;
    }
    if (last > maximumOrder)
      ordered.forEach((item, index) => orders.set(item.id, index * 10));
    return [...document.nodes, inserted].map((item) =>
      item.displayOrder === orders.get(item.id)
        ? item
        : { ...item, displayOrder: orders.get(item.id)! },
    );
  }
  // Branches do not have a total execution order. Place new work next to its chosen source.
  let order =
    validOrder(source.displayOrder) && source.displayOrder < maximumOrder
      ? source.displayOrder + 1
      : 0;
  while (occupied.has(order) && order < maximumOrder) order++;
  if (occupied.has(order)) {
    order = 0;
    while (occupied.has(order)) order++;
  }
  return [...document.nodes, { ...inserted, displayOrder: order }];
}

/** Split an explicit forward path, retaining its result, identity, triggers and unrelated metadata. */
export function insertStepOnWorkflowPath(
  document: WorkflowDefinitionDocument,
  newNode: WorkflowDefinitionNode,
  edgeId: string,
): WorkflowDefinitionDocument {
  const selected = path(document, edgeId),
    reason = insertionReason(document, selected);
  if (reason) throw new Error(reason);
  if (
    !['human_step', 'automated_step'].includes(newNode.typeCode) ||
    !newNode.id?.trim() ||
    !newNode.stepCode?.trim() ||
    nodes(document).some(
      (item) => item.id === newNode.id || item.stepCode === newNode.stepCode,
    )
  )
    throw new Error('Choose unused technical keys for the new work step.');
  const owner = outcomeOwner(document, selected);
  const source = node(document, selected.fromNodeId),
    target = node(document, selected.toNodeId);
  const orderedNodes = insertionOrders(document, source, target, newNode);
  const inserted = orderedNodes[orderedNodes.length - 1];
  const first = { ...selected, toNodeId: inserted.id };
  const intermediate = {
    ...document,
    nodes: updateOwner(
      { ...document, nodes: orderedNodes },
      owner,
      inserted.id,
    ),
    edges: document.edges.map((item) => (item === selected ? first : item)),
  };
  return replaceWorkflowPath(intermediate, {
    id: '',
    fromNodeId: inserted.id,
    toNodeId: selected.toNodeId,
  });
}
